'use client';

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Drawer, IconButton, Tabs, Text, Badge } from '@terra/ui-design-system';
import { InformationCircleIcon, Cancel01Icon } from '@terra/ui-icons';
import { ReactFlowProvider } from '@xyflow/react';
import type { ApplicationEntryPoint } from '@terra/shared-schemas-git';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useAppTargetId } from '../../store/projectStore';
import { CodeGraph } from '@terra/ui-charts';
import { useRepositoryInfoMap } from '../../hooks/useRepositoryInfoMap';

type SubEntryPoint = {
  method: string;
  routePath?: string | null;
  params?: string | null;
  description: string;
  codeSnippet: string;
  enhancement?: {
    fullPath: string;
    description: string;
    graphId: string;
    params?: string | null;
  } | null;
  endpointId?: string | null;
  reachable?: boolean | null;
};

export type EntryPoint = {
  fileName?: string | null;
  description?: string | null;
  codeSnippet?: string | null;
  entryPoint?: ApplicationEntryPoint['entryPoint'] | null;
  id: string;
  investigated?: boolean | null;
  isReachable?: boolean | null;
  subEntryPoints?: SubEntryPoint[];
};
interface CodeChangeGraphDrawerProps {
  entryPoint: EntryPoint | null;
  isOpen: boolean;
  onClose: () => void;
}

export function CodeChangeGraphDrawer({
  entryPoint,
  isOpen,
  onClose,
}: CodeChangeGraphDrawerProps) {
  const appTargetId = useAppTargetId();
  const [activeTabIndex, setActiveTabIndex] = useState('0');

  useEffect(() => {
    if (!isOpen) {
      setActiveTabIndex('0');
    }
  }, [isOpen, entryPoint]);
  const currentSubEntryPoint =
    entryPoint?.subEntryPoints?.[parseInt(activeTabIndex, 10)] || null;
  const codeGraphId = currentSubEntryPoint?.enhancement?.graphId;
  const description = currentSubEntryPoint?.enhancement?.description;
  const fullPath = currentSubEntryPoint?.enhancement?.fullPath;
  // Fetch code graph
  const { data: codeGraph } = useQuery({
    queryKey: ['code-graph', codeGraphId],
    queryFn: async () => {
      if (!codeGraphId) return null;
      const result = await terraProxyApiClient.getCodeGraphById(codeGraphId);

      return result || null;
    },
    enabled: !!codeGraphId && isOpen,
    refetchOnWindowFocus: false,
  });
  const repositoryInfoMap = useRepositoryInfoMap({
    applicationId: appTargetId,
  });

  return (
    <Drawer.Root open={isOpen} onOpenChange={({ open }) => !open && onClose()}>
      <Drawer.Backdrop />
      <Drawer.Positioner w="80vw">
        <Drawer.Content h="full">
          <Drawer.Header borderBottomWidth="1px" borderColor="border.subtle">
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <VStack alignItems="flex-start" gap={0}>
                <Text fontSize="sm" fontWeight="medium">
                  {entryPoint?.fileName || 'Attack Surface Changes'}
                </Text>
                {fullPath && (
                  <Text fontSize="xs" color="fg.muted">
                    {fullPath}
                  </Text>
                )}
              </VStack>
              <Drawer.CloseTrigger asChild>
                <IconButton size="xs" variant="ghost" aria-label="Close">
                  <Cancel01Icon size={16} />
                </IconButton>
              </Drawer.CloseTrigger>
            </HStack>
          </Drawer.Header>

          <Drawer.Body p={0}>
            <VStack gap={0} alignItems="stretch" w="full" h="full">
              {/* Tabs for sub-entry points */}
              {entryPoint?.subEntryPoints &&
                entryPoint.subEntryPoints.length > 1 && (
                  <Box borderBottomWidth="1px" borderColor="border.subtle">
                    <Tabs.Root
                      value={activeTabIndex}
                      lazyMount
                      unmountOnExit
                      onValueChange={(details) => {
                        if (details.value) {
                          setActiveTabIndex(details.value);
                        }
                      }}
                    >
                      <Tabs.List>
                        {entryPoint.subEntryPoints.map((subEp, index) => (
                          <Tabs.Trigger
                            key={index}
                            value={`${index}`}
                            disabled={!subEp.enhancement?.graphId}
                            fontSize="xs"
                            px={3}
                            py={2}
                          >
                            {`${subEp.method} ${subEp.routePath || ''}`}
                          </Tabs.Trigger>
                        ))}
                      </Tabs.List>
                      <Tabs.Indicator />
                    </Tabs.Root>
                  </Box>
                )}

              {/* Enhancement info - minimal */}
              {currentSubEntryPoint?.enhancement && (
                <HStack
                  gap={3}
                  px={4}
                  py={2}
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  bg="bg.subtle"
                  fontSize="xs"
                >
                  {description && (
                    <Text color="fg.muted" flex={1}>
                      {description}
                    </Text>
                  )}
                  {currentSubEntryPoint.reachable && (
                    <Badge size="sm" variant="emphasis">
                      Reachable
                    </Badge>
                  )}
                </HStack>
              )}

              {/* Code Graph */}
              <Box flex={1} overflow="hidden">
                {codeGraphId && codeGraph ? (
                  <ReactFlowProvider>
                    <CodeGraph
                      data={codeGraph}
                      repositoryInfoMap={repositoryInfoMap}
                      onNodeClick={({ id }) => {
                        console.log('Node clicked:', id);
                      }}
                    />
                  </ReactFlowProvider>
                ) : (
                  <Box
                    h="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <VStack gap={2}>
                      <InformationCircleIcon
                        size={24}
                        color="var(--colors-fg-muted)"
                      />
                      <Text fontSize="sm" color="fg.muted">
                        {entryPoint?.investigated
                          ? 'No code graph available'
                          : 'Not yet investigated'}
                      </Text>
                    </VStack>
                  </Box>
                )}
              </Box>
            </VStack>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
